import { useState } from "react";
import type { ExhibitImage } from "@/lib/api";
import { updateExhibitImage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { colors } from "@/lib/theme";

const FALLBACK_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='1'%3E%3Crect x='3' y='3' width='18' height='18' rx='2'/%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'/%3E%3Cpath d='M21 15l-5-5L5 21'/%3E%3C/svg%3E";

interface ImageEditorProps {
  image: ExhibitImage;
  exhibitId: number;
  pin: string;
  onDelete: () => void;
  onUpdate: (updated: ExhibitImage) => void;
}

/**
 * Expandable editor for individual exhibit images
 * Allows editing title and caption for each image
 */
export function ImageEditor({ image, exhibitId, pin, onDelete, onUpdate }: ImageEditorProps) {
  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState(image.title || "");
  const [caption, setCaption] = useState(image.caption || "");
  const [saving, setSaving] = useState(false);

  const hasChanges = title !== (image.title || "") || caption !== (image.caption || "");

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateExhibitImage(
        exhibitId,
        image.id,
        { title: title || null, caption: caption || null },
        pin
      );
      onUpdate(updated);
    } catch (err) {
      console.error("Failed to save:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden" style={{ borderColor: colors.border }}>
      {/* Collapsed header */}
      <ImageEditorHeader
        image={image}
        title={title}
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
        onDelete={onDelete}
      />

      {/* Expanded form */}
      {expanded && (
        <ImageEditorForm
          imageId={image.id}
          title={title}
          caption={caption}
          hasChanges={hasChanges}
          saving={saving}
          onTitleChange={setTitle}
          onCaptionChange={setCaption}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

/**
 * Header row showing thumbnail, title, and actions
 */
interface ImageEditorHeaderProps {
  image: ExhibitImage;
  title: string;
  expanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
}

function ImageEditorHeader({ image, title, expanded, onToggle, onDelete }: ImageEditorHeaderProps) {
  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50">
      <img
        src={image.thumbnailPath}
        alt=""
        className="w-16 h-16 object-cover rounded"
        style={{ backgroundColor: colors.border }}
        onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }}
      />

      <div className="flex-1">
        <p className="font-medium text-sm" style={{ color: colors.text }}>
          {title || <span className="italic text-gray-400">No title</span>}
        </p>
        <span
          className="text-xs px-2 py-0.5 rounded"
          style={{ backgroundColor: colors.border, color: colors.textMuted }}
        >
          {image.aspectRatio}
        </span>
      </div>

      <button type="button" onClick={onToggle} className="p-2 hover:bg-gray-200 rounded">
        {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>

      <button
        type="button"
        onClick={onDelete}
        className="p-2 hover:bg-red-100 rounded text-red-500"
      >
        <Trash2 size={18} />
      </button>
    </div>
  );
}

/**
 * Form for editing image title and caption
 */
interface ImageEditorFormProps {
  imageId: number;
  title: string;
  caption: string;
  hasChanges: boolean;
  saving: boolean;
  onTitleChange: (value: string) => void;
  onCaptionChange: (value: string) => void;
  onSave: () => void;
}

function ImageEditorForm({
  imageId,
  title,
  caption,
  hasChanges,
  saving,
  onTitleChange,
  onCaptionChange,
  onSave,
}: ImageEditorFormProps) {
  return (
    <div className="p-4 space-y-3 border-t" style={{ borderColor: colors.border }}>
      <div>
        <Label htmlFor={`title-${imageId}`} className="text-sm">
          Image Title
        </Label>
        <Input
          id={`title-${imageId}`}
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="Enter image title"
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor={`caption-${imageId}`} className="text-sm">
          Caption
        </Label>
        <Textarea
          id={`caption-${imageId}`}
          value={caption}
          onChange={(e) => onCaptionChange(e.target.value)}
          placeholder="Enter caption or description"
          rows={3}
          className="mt-1"
        />
      </div>

      {hasChanges && (
        <Button
          type="button"
          size="sm"
          onClick={onSave}
          disabled={saving}
          style={{ backgroundColor: colors.primary, color: colors.surface }}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      )}
    </div>
  );
}
